import React, {useContext, useEffect, useRef, useState} from 'react';
import {Collapse, Form, Tag} from 'antd';
import axios from "@common/axios";
import {UserContext} from "@context/UserContext";
import CircularLoader from "@common/CircularLoader";
import Alert from "@common/Alert";
import ConceptCard from "./ConceptCard";
import SelectionSummary from "./SelectionSummary";

const SelectContent = ({ showError }) => {
    const {user} = useContext(UserContext);
    const [topicGroups, setTopicGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const alertRef = useRef(null);

    useEffect(() => {
        if (user.syllabusId) {
            axios.get(`/web/ui/nav/syllabus/${user.syllabusId}/topicGroups`)
                .then(response => {
                    setTopicGroups(response.data);
                })
                .finally(() => setLoading(false));
        }
    }, [user.syllabusId]);

    useEffect(() => {
        if (showError) {
            document.getElementById('contentPanel')?.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [showError]);

    const form = Form.useFormInstance();
    const selectedConcepts = Form.useWatch(['selectedConcepts'], form) || {};

    const hasSelection = Object.values(selectedConcepts).some(Boolean);

    const getTopicSelectedCount = (topic) =>
        topic.concepts.filter(c => selectedConcepts[c.conceptId]).length;

    if (loading) return <CircularLoader />;

    return (
        <div>
            <h2>Select Content</h2>
            <p className={'ch-lesson-planner-step-subtitle'}>Open a chapter and tick the sections to include in your lesson plan.</p>
            {showError && !hasSelection && (
                <div ref={alertRef} style={{ marginBottom: 16 }}>
                    <Alert
                        type="warning"
                        message="You haven't selected any content yet. Open a chapter below and select at least one concept to include in your lesson plan."
                    />
                </div>
            )}
            <div className={'ch-lesson-planner-select-content-layout'}>
                <div className={'ch-lesson-planner-select-content-main'}>
                    {topicGroups.map((topicGroup, index) => (
                        <div key={topicGroup.topicGroupId ?? index}>
                            {!topicGroup.hideName && <h3>{topicGroup.topicGroupName}</h3>}
                            <Collapse
                                items={topicGroup.topics.map(topic => {
                                    const selectedCount = getTopicSelectedCount(topic);
                                    return {
                                    key: topic.topicId,
                                    label: (
                                        <span style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                            <strong>{topic.topicName}</strong>
                                            {selectedCount > 0 && (
                                                <Tag color={'green'}>{selectedCount} Selected</Tag>
                                            )}
                                        </span>
                                    ),
                                    children: (
                                        <div className={'ch-lesson-planner-concept-grid'}>
                                            {topic.concepts.map(concept => (
                                                <Form.Item
                                                    key={concept.conceptId}
                                                    name={['selectedConcepts', concept.conceptId]}
                                                    valuePropName="checked"
                                                    noStyle
                                                >
                                                    <ConceptCard concept={concept} />
                                                </Form.Item>
                                            ))}
                                        </div>
                                    ),
                                    };
                                })}
                            />
                        </div>
                    ))}
                </div>
                <div className={'ch-lesson-planner-select-content-sidebar'}>
                    <SelectionSummary topicGroups={topicGroups} />
                </div>
            </div>
        </div>
    );
};

export default SelectContent;
